import {GlobeAltIcon} from '@heroicons/react/24/solid';
import React from 'react';
import Select from 'react-select';

import StatusBar from 'src/components/StatusBar';
import {useTimezoneWithShortcut} from 'src/components/TimezoneContext';
import {useTranslations} from 'src/components/TranslationsContext';
import {Site} from 'src/lib/api/project';
import {TimeZoneUTCDisplayName, now} from 'src/lib/dates';

export default function TimeZoneToggle({selectedSite}: {selectedSite: Site}) {
  const {timezoneMode, setTimezoneMode, isTimezoneToggleEnabled} = useTimezoneWithShortcut();
  const {locale, t} = useTranslations();

  const getFormattedTimeZoneName = React.useCallback(
    (timeZone: string) => {
      if (timeZone === 'UTC') return TimeZoneUTCDisplayName;
      const displayTimeZone = timeZone;
      const date = now(displayTimeZone).setLocale(locale);
      return `${date.offsetNameLong} (${date.offsetNameShort})`;
    },
    [locale]
  );

  const niceTimeZoneName = React.useMemo(() => {
    if (!selectedSite) return null;
    if (timezoneMode === 'utc') return TimeZoneUTCDisplayName;
    return getFormattedTimeZoneName(selectedSite.localTimeZone);
  }, [selectedSite, timezoneMode, getFormattedTimeZoneName]);

  const localTimeZoneDisplayName = React.useMemo(() => {
    if (!selectedSite) return t('localTime');
    return getFormattedTimeZoneName(selectedSite.localTimeZone);
  }, [selectedSite, getFormattedTimeZoneName, t]);

  return (
    <div className="flex items-center text-gray-600">
      {isTimezoneToggleEnabled ? (
        <div className="flex items-center">
          <GlobeAltIcon className="w-5 mr-1" />
          <Select
            className="w-auto"
            styles={{
              control: () => ({display: 'inline-flex'}),
              valueContainer: (rest) => ({
                ...rest,
                padding: 0,
                fontSize: '1rem',
                lineHeight: '1.5rem',
              }),
              indicatorSeparator: () => ({display: 'none'}),
              menu: (rest) => ({...rest, width: 'auto'}),
            }}
            options={[
              {value: 'siteLocal', label: localTimeZoneDisplayName},
              {value: 'utc', label: TimeZoneUTCDisplayName},
            ]}
            value={{
              value: timezoneMode,
              label: timezoneMode === 'utc' ? TimeZoneUTCDisplayName : localTimeZoneDisplayName,
            }}
            onChange={(option) =>
              option?.value && setTimezoneMode(option.value as 'utc' | 'siteLocal')
            }
            isSearchable={false}
            closeMenuOnSelect={true}
          />
        </div>
      ) : (
        <div className="flex items-center">
          <GlobeAltIcon className="w-5 mr-1" />
          <div>{niceTimeZoneName}</div>
        </div>
      )}
      <StatusBar
        items={
          isTimezoneToggleEnabled && timezoneMode == 'utc' && niceTimeZoneName
            ? [{value: niceTimeZoneName}]
            : []
        }
      />
    </div>
  );
}
